import { HousingUnit } from '@reliance/types/models/HousingUnit';
import { Station } from '@reliance/types/models/Station';
import { Facility } from '../../models/facility';
import { IMobileDevice } from '../../models/mobile_device';
import config from '../../services/config';
import { getString, setString } from '../../strings';
import * as authActions from '../actions/auth';
import * as websiteActions from '../actions/website';

export interface ICommonState {
  facility: Facility;
  rehydrated: boolean;
  devicesWithoutGroups: IMobileDevice[];
  flaggedInmateDevices: number;
  stationsCount: number;
  disabledStationsCount: number;
  disabledStationsPreview: Partial<Station>[];
  housingUnitsWithCommunicationsAltered: HousingUnit[];
}

const initialState: ICommonState = {
  facility: null,
  rehydrated: false,
  flaggedInmateDevices: 0,
  devicesWithoutGroups: [],
  stationsCount: 0,
  disabledStationsCount: 0,
  disabledStationsPreview: [],
  housingUnitsWithCommunicationsAltered: []
};

function loadConfigFromFacility(facility: Facility) {
  config.name = facility.name;
  config.features = [];
  config.timezone = facility.timezone;

  facility.features.map((feature) => {
    switch (feature) {
      case 'Video Calls':
        config.features.push('video-call');
        break;
      case 'Reference Material':
        config.features.push('reference-material');
        break;
      case 'Job Search':
        config.features.push('job-search');
        break;
      case 'Inmate Requests':
        config.features.push('inmate-requests');
        break;
      case 'Commissary Ordering':
        config.features.push('commissary');
        break;
      case 'Law Library':
        config.features.push('law-library');
        break;
      case 'Kiosk Audio Calls':
        config.features.push('audio-call');
        break;
      case 'Wireless Audio Calls':
        config.features.push('wireless-audio-calls');
        break;
      case 'Wireless Video Calls':
        config.features.push('video-call');
        break;
      case 'Wireless Video Visitations':
        config.features.push('visitation');
        break;
      case 'Prepaid Collect Calls':
        config.features.push('audio-call');
        break;
      case 'Voicemail':
        config.features.push('voicemail');
        break;
      case 'Video Visitation':
        config.features.push('visitation');
        break;
      case 'Audio Visitation':
        config.features.push('audio-visitation');
        break;
      case 'Inmate Communication Policy':
        config.features.push('inmate-communication-policy');
        break;
      case 'Transcription':
        config.features.push('transcription');
        break;
      case 'Music':
        config.features.push('music');
        break;
      case 'Reference Materials':
        config.features.push('reference-material');
        break;
    }
  });

  if (facility.featureTexting) {
    config.features.push('texting');
  }

  // Stop configuration if we don't have the full facility data yet
  if (facility.getConfigurationValue == null) {
    return;
  }

  if (facility.getConfigurationValue('VisitTimeLimitMinutes')) {
    config.visitation.defaultTimeLimit = Number(
      facility.getConfigurationValue('VisitTimeLimitMinutes')
    );
  }

  if (facility.getConfigurationValue('VisitTimeLimitOptions')) {
    config.visitation.timeLimitOptions = JSON.parse(
      facility.getConfigurationValue('VisitTimeLimitOptions')
    );
  }

  if (facility.getConfigurationValue('InmatePINLength')) {
    config.inmate.pinLength = Number(
      facility.getConfigurationValue('InmatePINLength')
    );
  }

  if (facility.getConfigurationValue('RelcoreSiteBrand')) {
    setString('Brand', facility.getConfigurationValue('RelcoreSiteBrand'));
    setString(
      'ResultsApiError',
      getString('ResultsApiError').replace(
        'Reliance Telephone',
        facility.getConfigurationValue('RelcoreSiteBrand')
      )
    );
  }

  config.inmate.housingUnitsEnabled = facility.housingUnits?.length > 0;

  // If we get this value, assume that it is a site based site
  if (facility.getConfigurationValue('ConnectServerIp')) {
    config.connectServer.url = process.env.CONNECT_SERVER_URL ?? (location.protocol === 'https:' ? "wss://" : "ws://") + window.location.hostname + "/connect-server";
    config.nodeServer = process.env.APP_ENV == "development" ? process.env.NODE_SERVER : window.location.hostname;
  }
}

export default function (
  state: ICommonState = initialState,
  action
): ICommonState {
  switch (action.type) {
    case websiteActions.BASE_DATA_LOAD_SUCCESS:
      const baseDataFacility = new Facility(action.data);

      // Set config values
      loadConfigFromFacility(baseDataFacility);

      return {
        ...state,
        facility: baseDataFacility,
        housingUnitsWithCommunicationsAltered: baseDataFacility.housingUnits?.filter((unit: HousingUnit) => {
          if (unit.audioCallingEnabled != null && unit.videoCallingEnabled != null && unit.textingEnabled != null && unit.communicationsEnabled != null) {
            return !unit.audioCallingEnabled || !unit.videoCallingEnabled || !unit.textingEnabled || !unit.communicationsEnabled
          }
          return false;
        })
      };

    case authActions.AUTH_TOKEN_LOAD_SUCCESS:
      const facility = new Facility(
        action.accessToken.data.facilityUser.facility
      );

      // Set config values
      loadConfigFromFacility(facility);

      return {
        ...state,
        facility,
        housingUnitsWithCommunicationsAltered: facility.housingUnits.filter((unit: HousingUnit) => {
          if (unit.audioCallingEnabled != null) {
            return !unit.audioCallingEnabled;
          }
          return false;
        })
      };

    case 'NODE_SERVER/STATS':
      const statsFacility = new Facility(action.data.facility);

      return {
        ...state,
        flaggedInmateDevices: action.data.flaggedInmateDevices,
        devicesWithoutGroups: action.data.devicesWithoutGroups,
        stationsCount:
          action.data.stationsCount != undefined
            ? action.data.stationsCount
            : state.stationsCount,
        disabledStationsCount:
          action.data.disabledStationsCount != undefined
            ? action.data.disabledStationsCount
            : state.disabledStationsCount,
        disabledStationsPreview:
          action.data.disabledStationsPreview != undefined
            ? action.data.disabledStationsPreview
            : state.disabledStationsPreview,
        facility: statsFacility
      };

    case 'persist/REHYDRATE':
      const newState = {...state};

      // Track if we rehydrated the root state
      if (action.key == 'root') {
        newState.rehydrated = true;
      }

      // Set facility if available
      if (
        action &&
        action.payload &&
        action.payload.common &&
        action.payload.common.facility
      ) {
        const facility = new Facility(action.payload.common.facility);

        // Set config values
        loadConfigFromFacility(facility);

        return {
          ...newState,
          facility: facility
        };
      }

      return newState;
  }

  return state;
}
